// Ultimate Card Game (UCG) - Bot AI gambits (hidden match plans)
//
// A gambit is a plan the bot follows for a stretch of a match: a leaning in
// what it plays, how big its waves are and how patient its superweapon is,
// with the conditions under which it is worth it, done, or given up. It
// changes what the bot LOOKS like it is trying - never the reflexes (anti-air
// when flyers arrive, defending a base under attack, the anti-suicide gates
// of the spells): those stay stronger than any plan, because a gambit only
// nudges scores and parameters (docs/AI_MASTERPLAN_2026-10.md, 6.5).
//
// Hidden by design (E6): no name or id of a gambit appears in any UI, taunt or
// post-match screen; tests/ai_gambit_test.mjs checks it. Gambits are about
// variety and a feeling of intent, not strength: measured, the way a bot
// times and orders its plays does not move its win rate, so the guard rail is
// that the difficulty ladder must not move.
//
// Gambit fields:
//   id, label       internal only
//   races           deck races it is offered to ('any' = all)
//   requires(deck)  does the bot's own deck hold what the plan needs
//   prior           bandit prior weight per doctrine id (js/data/aiDoctrines.js)
//   timeboxSec      give up after this long without finishing
//   intent          what it changes while active:
//                     pairHoldBias   extra score against breaking a fusion pair (negative)
//                     waves          { thresholdMul, defendFactorMul } on the wave planner
//                     nukeThetaOffset  added to the superweapon threshold (never negative:
//                                    firing more eagerly measured worse)
//                     bias(card, ctx)  score points for a card (only reorders plays within a wave)
//                     hold(card, ctx, hand)  keep this card in hand for now - the part of a plan
//                                    a player can actually SEE, because every drawn card is played
//                                    sooner or later and only WHEN differs
//   success(ctx)    plan achieved -> back to normal
//   abort(ctx)      plan hopeless or no longer wanted -> back to normal / another plan
//
// ctx (js/engine/ai/strategist.js): t, tSince, baseHp, enemyBaseHp, pushRatio,
// ownValue, enemyValue, enemyInOwnHalf, enemyAntiAir, enemyNukeLive, stats.

import { cardHasRole } from '../../data/aiCardHints.js';
import { getCardByName } from '../../data/cards.js';

// --- deck summary (what the bot's own deck can do) --------------------------

const CHEAP_COST = 9;
const FRONT_STRUCTURES = new Set(['Deployable Cannon', 'Monitor Lock', 'Claiming Pillar']);

export function summarizeDeck(deck) {
  const counts = {};
  for (const item of deck || []) {
    const name = typeof item === 'string' ? item : item?.name;
    if (name) counts[name] = (counts[name] || 0) + 1;
  }
  const names = Object.keys(counts);
  const cards = names.map(n => getCardByName(n)).filter(Boolean);
  const fusible = (c) => !(c.isSpell || c.type === 'Spell' || c.isStructure || c.isModule) && c.race === 'Human' && !!getCardByName(`Hyper ${c.name}`);
  const has = (name) => counts[name] > 0;
  return {
    counts,
    fusionPairs: names.filter(n => { const c = getCardByName(n); return c && fusible(c) && counts[n] >= 2; }).length,
    hasPulp: has('Pulp'),
    fusibleCards: cards.filter(fusible).length,
    air: cards.filter(c => c.isAir || c.elementType === 'Air').reduce((s, c) => s + counts[c.name], 0),
    cheap: cards.filter(c => !c.isSpell && (c.cost || 0) <= CHEAP_COST).reduce((s, c) => s + counts[c.name], 0),
    frontStructures: names.filter(n => FRONT_STRUCTURES.has(n) || n === 'Fodder').length,
    hasOverstimulation: has('Forced Overstimulation'),
    hasMetamorphosis: has('Experimental Metamorphosis'),
    hasMentalExtraction: has('Mental Extraction'),
    hasSling: has('Invasion Capsule Sling'),
    economyCards: ['The Salvage Company', 'Claiming Pillar'].filter(has).length,
    marines: counts.Marine || 0,
    shades: counts.Shade || 0,
    outrangers: cards.filter(c => !c.isStructure && cardHasRole(c, 'outranger')).reduce((s, c) => s + counts[c.name], 0),
    cards: cards.length
  };
}

// --- what a plan needs from the deck (deck coupling) ------------------------------
//
// core(race, deck) -> the cards to put in (names) if the deck cannot carry the plan,
// or null when the race has nothing to offer for it. Used by the procedural deck
// builder (js/data/aiDecks.js coupleDeckToGambit) so a plan chosen first gets a
// deck that can play it.

const CORE = {
  Human: { cheap: ['Marine', 'Devil Patrol', 'Hot Hand'], front: ['Deployable Cannon'], air: ['Rocketship', 'A9 Piranha'] },
  Sul: { cheap: ['Apostate Loaf', 'Forgotten Loaf', 'Defective Loaf'], front: ['Claiming Pillar'], air: ['Delving Pillar', 'Sentinal Lock'] }
};
const coreFor = (race, kind) => (CORE[race] || CORE.Human)[kind];

// --- helpers for the intents --------------------------------------------------

const role = (card, r) => cardHasRole(card, r);
const countInHand = (hand, name) => hand.filter(x => x && x.name === name && !x.hidden && !x.pending).length;
const isHeavy = (card) => (card.cost || 0) >= 30;
const isCheapUnit = (card) => !card.isSpell && !card.isStructure && !card.isModule && (card.cost || 0) <= CHEAP_COST;

// Frozen all the way down: the plans are shared by every bot in a session.
function deepFreeze(o) {
  for (const v of Object.values(o)) if (v && typeof v === 'object' && !Object.isFrozen(v)) deepFreeze(v);
  return Object.freeze(o);
}

export const GAMBITS = deepFreeze({
  // Neutral: no plan. Always an arm of the bandit, so the bot can also just play.
  standard: {
    id: 'standard',
    races: 'any',
    requires: () => true,
    core: () => [],
    prior: {},
    timeboxSec: Infinity,
    intent: { pairHoldBias: 0, waves: null, nukeThetaOffset: 0, bias: () => 0, hold: () => false },
    success: () => false,
    abort: () => false
  },

  // G1: play for the fusion - keep pairs together, draw for the third, field the Hyper at once.
  hyperHunt: {
    id: 'hyperHunt',
    races: ['Human'],
    requires: (d) => d.fusionPairs >= 1 || (d.hasPulp && d.fusibleCards >= 1),
    // A second copy of a fusible card the deck already plays, else two Marines.
    core: (race, deck) => {
      if (race !== 'Human') return null;
      const names = Object.keys(summarizeDeck(deck).counts);
      const fusible = names.find(n => getCardByName(n)?.race === 'Human' && getCardByName(`Hyper ${n}`) && !getCardByName(n).isSpell);
      return fusible ? [fusible] : ['Marine', 'Marine'];
    },
    prior: { artillery: 0.2, standard: 0.1 },
    timeboxSec: 120,
    intent: {
      pairHoldBias: -14,
      waves: null,
      nukeThetaOffset: 0,
      bias: (card) => (card.type === 'Melt' ? 30 : 0),
      // A card that is half of a fusion pair stays in hand while there is a slot to draw the third into.
      hold: (card, c, hand) => card.type !== 'Melt' && !card.isSpell && !card.isStructure && countInHand(hand, card.name) === 2 && hand.some(x => x === null)
    },
    success: (c) => c.stats.hypersPlayed >= 1,
    abort: (c) => c.baseHp < 0.5 || (c.tSince > 90 && c.stats.hypersPlayed === 0)
  },

  // G2: let them come, hold the line with armour and structures, then answer with one big wave.
  turtleCounter: {
    id: 'turtleCounter',
    races: 'any',
    requires: () => true,
    core: () => [],
    prior: { bastion: 0.5, artillery: 0.3 },
    timeboxSec: 150,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 1.35, defendFactorMul: 0.85 },
      nukeThetaOffset: 0,
      bias: (card) => (role(card, 'tank') ? 8 : 0) + (role(card, 'frontlineStructure') ? 8 : 0) + (role(card, 'splash') ? 5 : 0) - (role(card, 'kamikaze') ? 6 : 0),
      // The fast strikers wait for the counter-push.
      hold: (card, c) => (role(card, 'kamikaze') || role(card, 'fast')) && c.stats.defenseWonAt === null
    },
    // The attack came, was beaten back: time for the counter-push.
    success: (c) => c.stats.defenseWonAt !== null,
    abort: (c) => c.tSince > 45 && c.stats.enemyAttackSeen === false
  },

  // G3: show cheap bodies and keep the heavies back until the enemy has spent its superweapon.
  nukeBait: {
    id: 'nukeBait',
    races: 'any',
    requires: (d) => d.cheap >= 3,
    core: (race) => coreFor(race, 'cheap'),
    prior: { rush: 0.3 },
    timeboxSec: 70,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 1.15, defendFactorMul: 1 },
      nukeThetaOffset: 8,
      bias: (card) => (isHeavy(card) ? -18 : 0) + (isCheapUnit(card) ? 10 : 0),
      // The heavies stay hidden until the enemy has spent its superweapon.
      hold: (card, c) => isHeavy(card) && !card.isSpell && c.enemyNukeFiredAt === null
    },
    // Done when the enemy fires: the heavies come out (see Strategist, recover flood).
    success: (c) => c.enemyNukeFiredAt !== null,
    abort: (c) => c.baseHp < 0.5 || c.tSince > 65 || (!c.enemyNukeLive && c.enemyNukeFiredAt === null)
  },

  // G4: push deep, then put the structures and Fodder at the front.
  fortressFront: {
    id: 'fortressFront',
    races: 'any',
    requires: (d) => d.frontStructures >= 1,
    core: (race) => coreFor(race, 'front'),
    prior: { bastion: 0.5 },
    timeboxSec: 160,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 1.1, defendFactorMul: 1 },
      nukeThetaOffset: 0,
      bias: (card, c) => ((role(card, 'frontlineStructure') || card.name === 'Fodder') ? (c.pushRatio >= 0.1 ? 16 : 4) : 0),
      // Front structures and Fodder only once the front is deep enough to deserve them.
      hold: (card, c) => (role(card, 'frontlineStructure') || card.name === 'Fodder') && c.pushRatio < 0.1
    },
    success: (c) => c.stats.frontStructuresAtDepth >= 1,
    abort: (c) => c.tSince > 40 && c.pushRatio < 0.02 && c.t > 70
  },

  // G5: build one big army instead of waves, then spend it all.
  overchargeAllin: {
    id: 'overchargeAllin',
    races: 'any',
    requires: (d) => d.hasOverstimulation,
    core: (race) => (race === 'Sul' ? ['Forced Overstimulation'] : null),
    prior: { rush: 0.5 },
    timeboxSec: 140,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 1.5, defendFactorMul: 1 },
      nukeThetaOffset: 0,
      // The spell's own anti-suicide gates still decide (-999 stays -999).
      bias: (card) => (role(card, 'allIn') ? 20 : 0),
      hold: () => false
    },
    success: (c) => c.stats.overstimulationCast,
    abort: (c) => c.baseHp < 0.5 || (c.tSince > 20 && c.ownValue > 0 && c.enemyValue > 2.5 * c.ownValue)
  },

  // G6: when the enemy has little that can shoot up, win from the air.
  airSwitch: {
    id: 'airSwitch',
    races: 'any',
    requires: (d) => d.air >= 2,
    core: (race) => coreFor(race, 'air'),
    prior: { airWing: 0.6 },
    timeboxSec: 140,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 1.15, defendFactorMul: 1 },
      nukeThetaOffset: 0,
      bias: (card, c) => (role(card, 'air') ? (c.enemyAntiAir <= 1 ? 16 : -6) : 0),
      // While the sky is open and there are flyers in hand, the heavy ground units wait.
      hold: (card, c, hand) => !card.isAir && !card.isSpell && !card.isStructure && (card.cost || 0) >= 15 && c.enemyAntiAir <= 1 && c.tSince < 30 && hand.some(x => x && (x.isAir || x.elementType === 'Air'))
    },
    success: (c) => c.stats.airPlayed >= 3,
    abort: (c) => c.enemyAntiAir >= 3 && c.tSince > 30
  },

  // G7: economy first - the plasma-maker goes down behind a quiet front, the waves stay smaller meanwhile.
  greed: {
    id: 'greed',
    races: 'any',
    requires: (d) => d.economyCards >= 1,
    core: (race) => (race === 'Sul' ? ['Claiming Pillar'] : ['The Salvage Company']),
    prior: { bastion: 0.4, standard: 0.2 },
    timeboxSec: 120,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 0.9, defendFactorMul: 1.1 },
      nukeThetaOffset: 0,
      bias: (card, c) => (role(card, 'economy') ? (c.enemyInOwnHalf < 10 ? 18 : -10) : 0),
      // The other centrepieces wait while the front is quiet and the economy card is not down yet.
      hold: (card, c, hand) => isHeavy(card) && !card.isSpell && !role(card, 'economy') && c.stats.economyPlayed === false && c.enemyInOwnHalf < 5 && c.tSince < 40 && hand.some(x => x && role(x, 'economy'))
    },
    success: (c) => c.stats.economyPlayed,
    abort: (c) => c.baseHp < 0.5 || (c.tSince > 20 && c.enemyInOwnHalf >= 25)
  },

  // G8: let them over-invest, then rewrite the board (Sul's Experimental Metamorphosis).
  metamorphosisTrap: {
    id: 'metamorphosisTrap',
    races: 'any',
    requires: (d) => d.hasMetamorphosis,
    core: (race) => (race === 'Sul' ? ['Experimental Metamorphosis'] : null),
    prior: { bastion: 0.4, artillery: 0.3 },
    timeboxSec: 150,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 1.25, defendFactorMul: 0.9 },
      nukeThetaOffset: 0,
      // The spell's own gates stay in force (-999 stays -999).
      bias: (card) => (role(card, 'boardReset') ? 20 : 0),
      // The trap stays set until their board is worth clearly more than ours.
      hold: (card, c) => role(card, 'boardReset') && c.enemyValue < Math.max(50, 1.4 * c.ownValue)
    },
    success: (c) => c.stats.metamorphosisCast,
    abort: (c) => c.baseHp < 0.4
  },

  // G9: steal what they hold (Human's Mental Extraction) once our own hand has room for it.
  mindTheft: {
    id: 'mindTheft',
    races: 'any',
    requires: (d) => d.hasMentalExtraction,
    core: (race) => (race === 'Human' ? ['Mental Extraction'] : null),
    prior: { artillery: 0.2, standard: 0.2 },
    timeboxSec: 100,
    intent: {
      pairHoldBias: 0,
      waves: null,
      nukeThetaOffset: 0,
      bias: (card) => (role(card, 'steal') ? 20 : 0),
      // Play the rest first: the spell wants a roomy hand.
      hold: (card, c, hand) => role(card, 'steal') && hand.filter(x => x === null).length < 2 && c.tSince < 45
    },
    success: (c) => c.stats.mentalExtractionCast,
    abort: (c) => c.baseHp < 0.4
  },

  // G10: outrange them - the long-range units come once a front stands in front of them.
  outrangeSiege: {
    id: 'outrangeSiege',
    races: ['Human'],
    requires: (d) => d.outrangers >= 2,
    core: (race) => (race === 'Human' ? ['Shade', 'Stickman'] : null),
    prior: { bastion: 0.4, artillery: 0.5 },
    timeboxSec: 150,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 1.1, defendFactorMul: 1 },
      nukeThetaOffset: 0,
      bias: (card) => (role(card, 'outranger') && !card.isStructure ? 14 : 0),
      hold: (card, c, hand) => role(card, 'outranger') && !card.isStructure && c.pushRatio < 0.05 && c.tSince < 45 && hand.some(x => x && x !== card && role(x, 'frontline'))
    },
    success: (c) => c.stats.outrangersPlayed >= 2,
    abort: (c) => c.tSince > 30 && c.enemyDetectors >= 1 && c.enemyAntiAir >= 2
  },

  // G11: a small wave to draw out their answer, the real one a few seconds later.
  feint: {
    id: 'feint',
    races: 'any',
    requires: () => true,
    core: () => [],
    prior: { rush: 0.5 },
    timeboxSec: 80,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 0.55, defendFactorMul: 1 },
      nukeThetaOffset: 0,
      bias: () => 0,
      hold: () => false
    },
    // The small wave is out: the strategist now schedules the real one (Strategist.tick).
    success: (c) => c.wavesReleased > c.stats.wavesAtStart,
    abort: (c) => c.baseHp < 0.5
  },

  // G12: the Capsule Sling early, Marines streamed through it.
  marineEngine: {
    id: 'marineEngine',
    races: ['Human'],
    requires: (d) => d.hasSling && d.marines >= 2,
    core: (race) => (race === 'Human' ? ['Invasion Capsule Sling', 'Marine', 'Marine'] : null),
    prior: { rush: 0.3, standard: 0.2 },
    timeboxSec: 130,
    intent: {
      pairHoldBias: 0,
      waves: { thresholdMul: 0.85, defendFactorMul: 1 },
      nukeThetaOffset: 0,
      bias: (card) => (card.name === 'Invasion Capsule Sling' ? 25 : (card.name === 'Marine' ? 8 : 0)),
      // The sling first.
      hold: (card, c, hand) => isHeavy(card) && !card.isSpell && !card.isModule && c.stats.slingPlayed === false && c.tSince < 30 && hand.some(x => x && x.name === 'Invasion Capsule Sling')
    },
    success: (c) => c.stats.slingPlayed && c.tSince > 40,
    abort: (c) => c.baseHp < 0.5
  },

  // G13: cloaked pressure - Shades first and early, until the enemy keeps a detector up.
  shadeHarass: {
    id: 'shadeHarass',
    races: ['Human'],
    requires: (d) => d.shades >= 1,
    core: (race) => (race === 'Human' ? ['Shade', 'Shade'] : null),
    prior: { rush: 0.3, artillery: 0.2 },
    timeboxSec: 120,
    intent: {
      pairHoldBias: 0,
      waves: null,
      nukeThetaOffset: 0,
      bias: (card) => (role(card, 'cloak') ? 18 : 0),
      hold: (card, c, hand) => isHeavy(card) && !card.isSpell && !role(card, 'cloak') && c.stats.shadesPlayed < 2 && c.tSince < 35 && c.enemyDetectors === 0 && hand.some(x => x && role(x, 'cloak'))
    },
    success: (c) => c.stats.shadesPlayed >= 3,
    abort: (c) => c.tSince > 30 && c.enemyDetectors >= 1
  }
});

export const GAMBIT_IDS = Object.freeze(Object.keys(GAMBITS));
// The plans a tier may follow (E6 keeps all of them hidden).
export const STARTER_POOL = Object.freeze(['hyperHunt', 'airSwitch', 'fortressFront']);
